import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { useAuth } from './useAuth';

/**
 * Hook to manage Google Contacts via Cloud Functions
 *
 * Note: This hook now uses Google Contacts as the source of truth via Cloud Functions.
 * Contact data comes from Google People API, with metadata (companyId, tags) stored in Firestore.
 *
 * Previously: Used Firestore real-time listeners
 * Now: Uses Cloud Functions to access Google Contacts
 */
export function useContacts() {
  const { currentUser } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load contacts from Google via Cloud Function
  const loadContacts = useCallback(async () => {
    if (!currentUser) {
      setContacts([]);
      setLoading(false);
      return;
    }

    console.log('[useContacts] Loading contacts from Google for user:', currentUser.uid);
    setLoading(true);

    try {
      const contactsListCallable = httpsCallable(functions, 'contactsListCallable');
      const result = await contactsListCallable({ pageSize: 1000 });

      console.log('[useContacts] Contacts loaded:', result.data.contacts?.length || 0);
      setContacts(result.data.contacts || []);
      setError(null);
    } catch (err) {
      console.error('[useContacts] Error loading contacts:', err);
      console.error('[useContacts] Error code:', err.code);
      console.error('[useContacts] Error message:', err.message);
      setError(err.message);
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Load contacts on mount and when user changes
  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  /**
   * Add new contact to Google Contacts
   *
   * @param {Object} contactData - Contact information
   * @param {string} contactData.name - Full name
   * @param {string} contactData.email - Email address
   * @param {string} [contactData.phone] - Phone number
   * @param {string} [contactData.companyId] - Firestore Company ID (will sync name to Google)
   * @param {string} [contactData.title] - Job title
   * @param {string} [contactData.notes] - Notes
   * @returns {Promise<string>} Google Contact ID
   */
  const addContact = async (contactData) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to add contacts');
    }

    try {
      console.log('[useContacts] Creating contact:', contactData.email);

      const contactsUpsertCallable = httpsCallable(functions, 'contactsUpsertCallable');
      const result = await contactsUpsertCallable(contactData);

      console.log('[useContacts] Contact created:', result.data.googleContactId);

      // Reload contacts to reflect the new contact
      await loadContacts();

      return result.data.googleContactId;
    } catch (err) {
      console.error('[useContacts] Error adding contact:', err);
      throw err;
    }
  };

  /**
   * Update existing contact in Google Contacts
   *
   * @param {string} contactId - Contact ID (can be Firestore ID or Google Contact ID)
   * @param {Object} updates - Fields to update
   */
  const updateContact = async (contactId, updates) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to update contacts');
    }

    try {
      console.log('[useContacts] Updating contact:', contactId);

      // Find the contact by either id or googleContactId
      const contact = contacts.find(c =>
        c.id === contactId || c.googleContactId === contactId
      );
      if (!contact) {
        console.error('[useContacts] Contact not found. Available contacts:', contacts.map(c => ({ id: c.id, googleContactId: c.googleContactId })));
        throw new Error('Contact not found');
      }

      // Merge current data with updates
      const contactData = {
        email: contact.email,
        name: contact.name,
        phone: contact.phone,
        companyId: contact.companyId,
        title: contact.title,
        notes: contact.notes,
        googleContactId: contact.googleContactId, // Critical: for direct update
        ...updates,
      };

      console.log('[useContacts] Sending update with googleContactId:', contact.googleContactId);

      const contactsUpsertCallable = httpsCallable(functions, 'contactsUpsertCallable');
      await contactsUpsertCallable(contactData);

      console.log('[useContacts] Contact updated:', contactId);

      // Reload contacts to reflect the update
      await loadContacts();
    } catch (err) {
      console.error('[useContacts] Error updating contact:', err);
      throw err;
    }
  };

  /**
   * Delete contact from Google Contacts
   *
   * Note: Currently not implemented in Cloud Functions.
   * Google People API delete requires special permissions.
   * For now, contacts should be manually deleted from Google Contacts UI.
   */
  const deleteContact = async (googleContactId) => {
    console.warn('[useContacts] Contact deletion not yet implemented');
    console.warn('[useContacts] Please delete contacts manually from Google Contacts');
    throw new Error('Contact deletion not yet implemented. Please delete from Google Contacts directly.');
  };

  return {
    contacts,
    loading,
    error,
    addContact,
    updateContact,
    deleteContact,
    refresh: loadContacts, // Expose refresh function for manual reload
  };
}
